import React, { Suspense, useMemo, useCallback } from "react";
import type { StageElement, ThemeVariant } from "../../../types";
import { TextbookTdx, TdxLoading, TdxError, TDX_IFRAME_OVERRIDE_CSS } from "./styles";
import { REMOTE_URLS } from "../../../constants";
import { loadRemoteModule } from "../../../util/loadRemoteModule";
import { mapBronteEnvToLtEnv } from "../../../util/envMapping";
import { inferBronteThemeWrapperClass } from "../../../util/bronteThemeClass";
import { readPluginConfigString } from "../../../util/readPluginConfigString";

interface TdxPluginComponentProps {
  element: StageElement;
  componentConfig?: any;
  getAuthToken?: (callback: (token: string) => void) => void;
  env?: string;
  themeVariant?: Partial<ThemeVariant>;
}

interface RemoteTdxProps {
  environment: string;
  assessmentType: "tdx" | "tdx2";
  isEmbedded: boolean;
  id: any;
  displaySummary: boolean;
  onEvent: (eventName: string, eventData: any) => void;
  auth: { get: () => Promise<{ type: "PI"; token: string | null }> };
  tdxContent: { reviewMode: boolean; item: { contentId: string } };
}

function createRemoteTdx(env: string | undefined) {
  const remoteUrls = REMOTE_URLS[env ?? "stg"] ?? REMOTE_URLS["stg"];
  return React.lazy(() =>
    loadRemoteModule(remoteUrls.TDX, "TDX", "./AssessmentItemFactory").then((mod) => ({
      default: mod.default || mod,
    })),
  ) as React.LazyExoticComponent<React.ComponentType<RemoteTdxProps>>;
}

export const TdxPluginComponent: React.FC<TdxPluginComponentProps> = ({
  componentConfig,
  getAuthToken: clientGetAuthToken,
  env,
  themeVariant,
}) => {
  const RemoteTdx = useMemo(() => createRemoteTdx(env), [env]);
  const bronteThemeWrapper = inferBronteThemeWrapperClass(themeVariant);

  const config = (componentConfig?.config ?? {}) as Record<string, unknown>;

  const assessmentItemContentId = readPluginConfigString(config, "assessmentItemContentId");
  const assessmentItemId = readPluginConfigString(config, "assessmentItemId");
  const useClassic = !!config.useClassic;

  const activityLabel = readPluginConfigString(config, "activityLabel");
  const activityTitle = readPluginConfigString(config, "activityTitle");
  const instructions = readPluginConfigString(config, "instructions");
  const captions = readPluginConfigString(config, "captions");
  const credit = readPluginConfigString(config, "credit");

  const environment = mapBronteEnvToLtEnv(env);

  const componentId = componentConfig.$id || "";

  const itemEventHandler = useCallback((eventName: string, eventData: any) => {
    console.log("[TDX Viewer-Express] Event:", eventName, eventData);
  }, []);

  const getAuth = useCallback((): Promise<{ type: "PI"; token: string | null }> => {
    if (!clientGetAuthToken) {
      return Promise.resolve({ type: "PI", token: null });
    }
    return new Promise((resolve) => {
      clientGetAuthToken((token: string) => {
        console.log("token from getAuth", token);
        resolve({ type: "PI", token: token ?? null });
      });
    });
  }, [clientGetAuthToken]);

  const assessmentItemProps = useMemo(() => {
    if (!assessmentItemContentId) return null;

    const assessmentType: "tdx" | "tdx2" = useClassic ? "tdx" : "tdx2";

    return {
      environment,
      assessmentType,
      isEmbedded: true,
      id: assessmentItemId || assessmentItemContentId,
      displaySummary: false,
      onEvent: itemEventHandler,
      auth: { get: getAuth },
      tdxContent: {
        reviewMode: false,
        item: { contentId: assessmentItemContentId },
      },
    };
  }, [
    environment,
    useClassic,
    assessmentItemId,
    assessmentItemContentId,
    itemEventHandler,
    getAuth,
  ]);

  if (!assessmentItemProps) {
    return (
      <TdxError>
        <p>Invalid TDX configuration: missing assessmentItemContentId</p>
      </TdxError>
    );
  }

  return (
    <TextbookTdx data-id={componentId} className={`plugin-tdx tdxFigureContainer ${bronteThemeWrapper}`.trim()}>
      <style>{TDX_IFRAME_OVERRIDE_CSS}</style>

      {(activityLabel || activityTitle) && (
        <h4 className="component-label-section">
          {activityLabel ? (
            <span
              className="component-label"
              data-id={`${componentId}-activityLabel`}
              dangerouslySetInnerHTML={{ __html: activityLabel }}
            />
          ) : null}
          {activityTitle ? (
            <span
              className="component-title"
              data-id={`${componentId}-activityTitle`}
              dangerouslySetInnerHTML={{ __html: activityTitle }}
            />
          ) : null}
        </h4>
      )}

      {instructions ? (
        <div
          className="component-instructions"
          data-id={`${componentId}-instructions`}
          dangerouslySetInnerHTML={{ __html: instructions }}
        />
      ) : null}

      <Suspense fallback={<TdxLoading>Loading assessment item...</TdxLoading>}>
        <RemoteTdx {...assessmentItemProps} />
      </Suspense>

      {captions ? (
        <figcaption>
          <div
            className="component-caption"
            data-id={`${componentId}-captions`}
            dangerouslySetInnerHTML={{ __html: captions }}
          />
        </figcaption>
      ) : null}

      {credit ? (
        <div
          className="component-credit"
          data-id={`${componentId}-credit`}
          dangerouslySetInnerHTML={{ __html: credit }}
        />
      ) : null}
    </TextbookTdx>
  );
};
